import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Avatar,
  IconButton,
  Popover,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import {
  InfoCard,
  SparkAccordion,
  SparkSearchField,
} from 'sharedComponents/UI';
import { useInfinitePluginSearch } from 'hooks/Plugins/useInfinitePluginSearch';
import { useAddChatPluginFunction } from 'hooks/chats/useAddChatPluginFunction';
import { useUpdateChatPluginFunction } from 'hooks/chats/useUpdateChatPluginFunction';
import { useBatchUpdateChatPluginFunctions } from 'hooks/chats/useBatchUpdateChatPluginFunctions';
import { useChatPluginFunctions } from 'hooks/chats/useChatPluginFunctions';

type PluginsPopoverProps = {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  chatID: string;
};

interface SelectedFunction {
  functionId: string;
  chatPluginFunctionId?: string; // ID returned from the API after adding
}

const PluginsPopover = ({
  open,
  anchorEl,
  onClose,
  chatID,
}: PluginsPopoverProps) => {
  const {
    plugins,
    searchQuery,
    handleSearch,
    handleLoadMore,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useInfinitePluginSearch();
  const {} = useChatPluginFunctions(chatID);
  const { mutateAsync: addChatPluginFunction } =
    useAddChatPluginFunction(chatID);
  const { mutateAsync: updateChatPluginFunction } =
    useUpdateChatPluginFunction(chatID);
  const { mutateAsync: batchUpdateChatPluginFunctions } =
    useBatchUpdateChatPluginFunctions(chatID);

  const scrollRef = useRef<HTMLDivElement>(null);
  // Updated state to store both function IDs and their corresponding chatPluginFunctionIds
  const [selectedFunctions, setSelectedFunctions] = useState<{
    [pluginId: string]: SelectedFunction[];
  }>({});

  // Observer for infinite scrolling
  const observer = useRef<IntersectionObserver | null>(null);
  const lastPluginElementRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          handleLoadMore();
        }
      },
      { threshold: 0.5 }
    );

    if (lastPluginElementRef.current) {
      observer.current.observe(lastPluginElementRef.current);
    }

    return () => {
      if (observer.current) observer.current.disconnect();
    };
  }, [hasNextPage, isFetchingNextPage, handleLoadMore]);

  const handleClearAll = async () => {
    // Get all chat plugin function IDs to delete in a batch
    const operations = Object.values(selectedFunctions)
      .flat()
      .filter((func) => func.chatPluginFunctionId)
      .map((func) => ({
        id: func.chatPluginFunctionId as string,
        isDeleted: true,
      }));

    if (operations.length > 0) {
      // Use batch update operation instead of individual calls
      await batchUpdateChatPluginFunctions({
        chatPluginFunctionOperations: operations,
      });
    }

    // Clear the state
    setSelectedFunctions({});
  };

  const handleRemoveFunction = async (pluginId: string, functionId: string) => {
    // Find the chatPluginFunctionID associated with this plugin function
    const chatPluginFunctionId = selectedFunctions[pluginId]?.find(
      (f) => f.functionId === functionId
    )?.chatPluginFunctionId;

    // If we have the chatPluginFunctionId, call the API to remove it
    if (chatPluginFunctionId) {
      await updateChatPluginFunction({
        chatPluginFunctionID: chatPluginFunctionId,
        attributes: {
          isDeleted: true,
        },
      });

      // Update local state after successful API call
      setSelectedFunctions((prev) => {
        const updatedFunctions = { ...prev };
        updatedFunctions[pluginId] = updatedFunctions[pluginId].filter(
          (func) => func.functionId !== functionId
        );

        if (updatedFunctions[pluginId].length === 0) {
          delete updatedFunctions[pluginId];
        }

        return updatedFunctions;
      });
    } else {
      // If no chatPluginFunctionId, just update the local state
      setSelectedFunctions((prev) => {
        const updatedFunctions = { ...prev };
        updatedFunctions[pluginId] = updatedFunctions[pluginId].filter(
          (func) => func.functionId !== functionId
        );

        if (updatedFunctions[pluginId].length === 0) {
          delete updatedFunctions[pluginId];
        }

        return updatedFunctions;
      });
    }
  };

  const handleAddFunction = async (pluginId: string, functionId: string) => {
    // Add the function to the chat plugin functions
    const response = await addChatPluginFunction({
      pluginID: pluginId,
      pluginFunctionID: functionId,
    });

    // Store the chatPluginFunctionId from the response
    const chatPluginFunctionId = response.data.id;

    // Update local state after successful API call
    setSelectedFunctions((prev) => {
      const updatedFunctions = { ...prev };

      if (!updatedFunctions[pluginId]) {
        updatedFunctions[pluginId] = [];
      }

      if (
        !updatedFunctions[pluginId].some(
          (func) => func.functionId === functionId
        )
      ) {
        updatedFunctions[pluginId] = [
          ...updatedFunctions[pluginId],
          {
            functionId,
            chatPluginFunctionId,
          },
        ];
      }

      return updatedFunctions;
    });
  };

  // Calculate the total selected plugins and functions
  const selectedPluginsCount = Object.keys(selectedFunctions).length;
  const totalSelectedFunctions = Object.values(selectedFunctions).reduce(
    (sum, functions) => sum + functions.length,
    0
  );

  // Get selected plugins with their functions
  const selectedPlugins = plugins
    .filter((plugin) => selectedFunctions[plugin.plugin.id])
    .map((plugin) => {
      const pluginId = plugin.plugin.id;
      const selectedFunctionIds =
        selectedFunctions[pluginId]?.map((f) => f.functionId) || [];

      return {
        ...plugin,
        pluginFunctions: plugin.pluginFunctions.filter((func) =>
          selectedFunctionIds.includes(func.id)
        ),
      };
    });

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      elevation={2}
      anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
      transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      disablePortal={true}
      keepMounted
      container={document.body}
      sx={{
        '& .MuiPopover-paper': {
          mt: '-5px',
          borderRadius: '8px',
          minWidth: 350,
          minHeight: '450px',
          maxWidth: 'none',
          position: 'fixed',
          transform: 'none !important',
          boxSizing: 'border-box',
        },
        minHeight: '400px',
      }}
    >
      <Box bgcolor='#f0f2ff' boxSizing='border-box'>
        {selectedPluginsCount > 0 && (
          <>
            <Box
              p={2}
              display='flex'
              justifyContent='space-between'
              alignItems='center'
            >
              <Box display='flex' alignItems='center'>
                {selectedPlugins.slice(0, 2).map((plugin) => (
                  <Avatar
                    key={plugin.plugin.id}
                    src={plugin.plugin.attributes.logoURL || ''}
                    alt={plugin.plugin.attributes.name || ''}
                    sx={{ width: 20, height: 20, mr: 0.5 }}
                  />
                ))}
                <Typography
                  variant='subtitle1'
                  fontWeight='medium'
                  sx={{ ml: 1 }}
                >
                  +{totalSelectedFunctions}
                </Typography>
              </Box>
              <Button
                variant='contained'
                size='small'
                onClick={handleClearAll}
                sx={{
                  bgcolor: '#6267AC',
                  '&:hover': {
                    bgcolor: '#4A4D82',
                  },
                }}
              >
                Clear
              </Button>
            </Box>

            <Box overflow='auto' maxHeight='34vh'>
              {selectedPlugins.map((plugin) => (
                <Box
                  key={plugin.plugin.id}
                  mb={1}
                  mx={2}
                  bgcolor='white'
                  borderRadius='8px'
                >
                  <SparkAccordion
                    title={
                      <Box display='flex' alignItems='center'>
                        <Avatar
                          src={plugin.plugin.attributes.logoURL || ''}
                          sx={{ width: 24, height: 24, mr: 1 }}
                        />
                        <Typography variant='subtitle2'>
                          {plugin.plugin.attributes.name} (
                          {plugin.pluginFunctions.length})
                        </Typography>
                      </Box>
                    }
                    initialExpand={true}
                    summaryStyles={{
                      cursor: 'pointer',
                      p: 1.5,
                      borderRadius: '8px',
                      border: '1px solid #E0E0E0',
                    }}
                    detailsStyle={{
                      border: '1px solid #E0E0E0',
                      borderTop: 'none',
                      borderRadius: '0 0 8px 8px',
                    }}
                    sx={{
                      border: 'none',
                    }}
                  >
                    {plugin.pluginAuthentications.length > 0 && (
                      <Box px={2} py={1}>
                        <Typography
                          variant='body2'
                          color='text.secondary'
                          fontWeight='medium'
                        >
                          *Connection
                        </Typography>

                        {plugin.pluginConnections.length > 0 ? (
                          <Box
                            mt={1}
                            p={1.5}
                            border='1px solid #E0E0E0'
                            borderRadius='8px'
                            display='flex'
                            alignItems='center'
                            justifyContent='space-between'
                          >
                            <Typography variant='body2'>
                              {plugin.pluginConnections[0].attributes.name ||
                                'Connection'}
                            </Typography>
                            <IconButton size='small'>
                              <MoreVertIcon fontSize='small' />
                            </IconButton>
                          </Box>
                        ) : (
                          <Box
                            mt={1}
                            p={1.5}
                            bgcolor='#FFEBEE'
                            borderRadius='8px'
                            display='flex'
                            alignItems='center'
                          >
                            <ErrorIcon
                              color='error'
                              fontSize='small'
                              sx={{ mr: 1 }}
                            />
                            <Typography variant='body2' color='error'>
                              Connection Error
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    )}

                    <Box px={2} py={1}>
                      <Typography variant='body2' fontWeight='medium'>
                        Functions
                      </Typography>

                      {plugin.pluginFunctions.map((func) => (
                        <Box
                          key={func.id}
                          mt={1}
                          p={1.5}
                          border='1px solid #E0E0E0'
                          borderRadius='8px'
                          display='flex'
                          alignItems='center'
                          justifyContent='space-between'
                          position='relative'
                          sx={{
                            '&:hover .delete-icon': {
                              display: 'block',
                            },
                          }}
                        >
                          <Typography variant='body2'>
                            {func.attributes.name}
                          </Typography>
                          <IconButton
                            size='small'
                            className='delete-icon'
                            onClick={() =>
                              handleRemoveFunction(plugin.plugin.id, func.id)
                            }
                            sx={{
                              display: 'none',
                              position: 'absolute',
                              right: 8,
                              top: '50%',
                              transform: 'translateY(-50%)',
                            }}
                          >
                            <DeleteIcon fontSize='small' />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  </SparkAccordion>
                </Box>
              ))}
            </Box>
          </>
        )}
      </Box>

      <Box>
        <Box p={2}>
          <SparkSearchField
            placeholder='Search Plugins'
            onChange={handleSearch}
            minLength={3}
          />
        </Box>

        <Box
          overflow='auto'
          maxHeight='34vh'
          minWidth={'250px'}
          px={2}
          pb={2}
          ref={scrollRef}
        >
          {isLoading && plugins.length === 0 ? (
            <Box display='flex' justifyContent='center' p={2}>
              <CircularProgress size={24} />
            </Box>
          ) : searchQuery.length > 0 && searchQuery.length < 3 ? (
            <Typography
              variant='body2'
              color='text.secondary'
              textAlign='center'
              p={2}
            >
              Type at least 3 characters to search
            </Typography>
          ) : plugins.length === 0 ? (
            <Box py={1}>
              <InfoCard type={'error'} info={'No Plugins found.'} />
            </Box>
          ) : (
            <>
              {plugins.map((plugin, index) => {
                const isLastElement = index === plugins.length - 1;

                return (
                  <Box
                    key={plugin.plugin.id}
                    mb={1}
                    ref={isLastElement ? lastPluginElementRef : null}
                    width={'100%'}
                  >
                    <SparkAccordion
                      title={
                        <Box display='flex' alignItems='center'>
                          <Avatar
                            src={plugin.plugin.attributes.iconURL || ''}
                            sx={{ width: 24, height: 24, mr: 1 }}
                          />
                          <Typography variant='subtitle2'>
                            {plugin.plugin.attributes.name}
                          </Typography>
                        </Box>
                      }
                      summaryStyles={{
                        cursor: 'pointer',
                        p: 1.5,
                      }}
                      collapseProps={{ sx: { px: 1 } }}
                      expandedSx={{
                        '&:hover': {
                          border: '1px solid #6267ac',
                        },
                      }}
                      sx={{
                        border: '1px solid #E0E0E0',
                        '&:hover': {
                          border: '1px solid #6267ac',
                        },
                      }}
                    >
                      {plugin.pluginFunctions.map((func) => (
                        <Box
                          key={func.id}
                          p={1.5}
                          border='1px solid #E0E0E0'
                          borderRadius={'8px'}
                          my={0.5}
                          sx={{
                            cursor: 'pointer',
                            '&:hover': { border: '1px solid #6267ac' },
                            '&:last-child': {
                              borderBottom: '0px',
                            },
                          }}
                          onClick={() =>
                            handleAddFunction(plugin.plugin.id, func.id)
                          }
                        >
                          <Typography variant='body2'>
                            {func.attributes.name}
                          </Typography>
                        </Box>
                      ))}
                      {plugin.pluginFunctions.length === 0 && (
                        <Box pt={1} pb={0.5}>
                          <InfoCard
                            type={'error'}
                            info={'No Functions available.'}
                          />
                        </Box>
                      )}
                    </SparkAccordion>
                  </Box>
                );
              })}

              {isFetchingNextPage && (
                <Box display='flex' justifyContent='center' p={2}>
                  <CircularProgress size={24} />
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>
    </Popover>
  );
};

export default PluginsPopover;
